import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openWindow, selectWindows } from "../../Slices/windowsSlice";
import { selectLayout, selectLayoutLoaded } from "./desktopLayoutSlice";

/**
 * useOpenWindowsSync — persists the set of open webtop windows to the
 * per-user desktopLayout (backend-synced) so they survive page refreshes,
 * and restores them the next time the webtop loads.
 *
 * Saved shape: layout.openWindows = [{ path, title }, ...]
 * Only path + title are stored; geometry/z-order are ephemeral per-session.
 *
 * @param {Function} updateLayout - debounced layout updater from useDesktopLayoutSync
 */
export const useOpenWindowsSync = (updateLayout) => {
  const dispatch = useDispatch();
  const windows = useSelector(selectWindows);
  const layout = useSelector(selectLayout);
  const layoutLoaded = useSelector(selectLayoutLoaded);

  // Tracks whether we've already run the one-time restore on this mount.
  const restoredRef = useRef(false);

  // Once: when the layout first loads, reopen any windows that were saved.
  // Skipped if windows are already present (user navigated in from the legacy
  // shell, which may have seeded some windows via the tab→window bridge).
  // Skipped entirely in tearoff windows (updateLayout is null) — the tearoff
  // window opens only its specific detached view, not the full saved set.
  useEffect(() => {
    if (!updateLayout || !layoutLoaded || restoredRef.current) return;
    restoredRef.current = true;

    const saved = layout?.openWindows;
    if (!Array.isArray(saved) || saved.length === 0) return;
    if (windows.length > 0) return;

    saved.forEach(({ path, title }) => {
      if (path) dispatch(openWindow({ path, title, deduplicate: true }));
    });
    // Intentionally only react to layoutLoaded flipping true — layout and
    // windows are read from current selector values at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutLoaded]);

  // Persist the open windows list whenever it changes. updateLayout is
  // debounced (1200 ms) so rapid open/close sequences collapse to one save.
  // Skipped in tearoff windows (updateLayout is null).
  useEffect(() => {
    if (!updateLayout || !layoutLoaded || !restoredRef.current) return;
    updateLayout({ openWindows: windows.map(({ path, title }) => ({ path, title })) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windows]);
};

export default useOpenWindowsSync;
